import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';

// Components
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';

// Pages
import Home from './pages/Home/Home';
import About from './pages/About/About';
import Projects from './pages/Projects/Projects';
import ProjectDetail from './pages/ProjectDetail/ProjectDetail';
import Contact from './pages/Contact/Contact';

// Theme Context for Dark/Light mode
const ThemeContext = createContext();
export const useThemeContext = () => useContext(ThemeContext);

// AWS Cloudscape Design System Colors
const getTheme = (isDark) => ({
  mode: isDark ? 'dark' : 'light',
  colors: {
    // AWS Brand Colors
    primary: '#FF9900', // AWS Orange
    primaryHover: '#EC7211',
    secondary: '#232F3E', // AWS Dark Blue
    accent: '#146EB4', // AWS Blue
    
    // Background Colors
    background: isDark ? '#0F1419' : '#FAFAFA',
    surface: isDark ? '#1D2329' : '#FFFFFF',
    surfaceSecondary: isDark ? '#242A31' : '#F2F3F3',
    
    // Text Colors
    textPrimary: isDark ? '#FFFFFF' : '#0D1117',
    textSecondary: isDark ? '#879196' : '#687078',
    textTertiary: isDark ? '#5F6B77' : '#879196',
    
    // Border Colors
    border: isDark ? '#414D5C' : '#D5DBDB',
    borderLight: isDark ? '#2C3541' : '#E9ECEF',
    
    // Status Colors
    success: '#037F0C',
    warning: '#8D6605',
    error: '#D91515',
    info: '#0073BB',
    
    // Interactive States
    hover: isDark ? '#414D5C' : '#F2F3F3',
    active: isDark ? '#232F3E' : '#E6F2FF',
    focus: '#0073BB',
    
    // AWS Service Colors
    compute: '#FF9900',
    storage: '#3F8624',
    database: '#C925D1',
    networking: '#8C4FFF',
    security: '#DD344C',
    analytics: '#01A88D',
  },
  
  // Typography matching AWS Design System
  fonts: {
    primary: '"Amazon Ember", "Helvetica Neue", Roboto, Arial, sans-serif',
    mono: '"Amazon Ember Mono", Monaco, "Courier New", monospace',
  },
  
  // Spacing scale
  spacing: {
    xs: '4px',
    s: '8px',
    m: '16px',
    l: '24px',
    xl: '32px',
    xxl: '40px',
  },
  
  // Border radius
  borderRadius: {
    small: '4px',
    medium: '8px',
    large: '12px',
  },
  
  // Shadows
  shadows: {
    subtle: isDark 
      ? '0 1px 1px rgba(0, 0, 0, 0.25)' 
      : '0 1px 1px rgba(0, 0, 0, 0.1)',
    container: isDark 
      ? '0 4px 8px rgba(0, 0, 0, 0.25)' 
      : '0 1px 4px rgba(0, 0, 0, 0.1)',
    dropdown: isDark 
      ? '0 6px 12px rgba(0, 0, 0, 0.25)' 
      : '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  
  // Breakpoints
  breakpoints: {
    xs: '576px',
    sm: '768px',
    md: '992px',
    lg: '1200px',
    xl: '1400px'
  }
});

// AWS Cloudscape Global Styles
const GlobalStyle = createGlobalStyle`
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  html {
    scroll-behavior: smooth;
  }
  
  body {
    font-family: ${props => props.theme.fonts.primary};
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.textPrimary};
    line-height: 1.6;
    overflow-x: hidden;
    min-height: 100vh;
    transition: background-color 0.3s ease, color 0.3s ease;
  }
  
  /* AWS Button Styles */
  .aws-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: ${props => props.theme.borderRadius.small};
    border: 1px solid;
    font-family: ${props => props.theme.fonts.primary};
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    cursor: pointer;
    transition: all 0.15s ease;
    text-decoration: none;
    
    &.primary {
      background-color: ${props => props.theme.colors.primary};
      border-color: ${props => props.theme.colors.primary};
      color: #FFFFFF;
      
      &:hover {
        background-color: ${props => props.theme.colors.primaryHover};
        border-color: ${props => props.theme.colors.primaryHover};
      }
      
      &:focus {
        outline: 2px solid ${props => props.theme.colors.focus};
        outline-offset: 2px;
      }
    }
    
    &.secondary {
      background-color: transparent;
      border-color: ${props => props.theme.colors.border};
      color: ${props => props.theme.colors.textPrimary};
      
      &:hover {
        background-color: ${props => props.theme.colors.hover};
      }
    }
    
    &.link {
      background-color: transparent;
      border-color: transparent;
      color: ${props => props.theme.colors.info};
      padding: 4px 8px;
      
      &:hover {
        background-color: ${props => props.theme.colors.active};
        text-decoration: underline;
      }
    }
  }
  
  /* AWS Container Styles */
  .aws-container {
    background-color: ${props => props.theme.colors.surface};
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: ${props => props.theme.borderRadius.medium};
    box-shadow: ${props => props.theme.shadows.container};
    transition: background-color 0.3s ease, border-color 0.3s ease;
  }
  
  /* AWS Header Styles */
  .aws-header {
    padding: 16px 24px;
    border-bottom: 1px solid ${props => props.theme.colors.border};
    background-color: ${props => props.theme.colors.surface};
    
    h1, h2, h3 {
      color: ${props => props.theme.colors.textPrimary};
      font-weight: 400;
      margin: 0;
    }
    
    h1 { font-size: 24px; line-height: 32px; }
    h2 { font-size: 20px; line-height: 28px; }
    h3 { font-size: 16px; line-height: 24px; }
  }
  
  /* AWS Content Styles */
  .aws-content {
    padding: 24px;
  }
  
  /* AWS Badge Styles */
  .aws-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: ${props => props.theme.borderRadius.small};
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    
    &.info {
      background-color: rgba(0, 115, 187, 0.1);
      color: ${props => props.theme.colors.info};
    }
    
    &.success {
      background-color: rgba(3, 127, 12, 0.1);
      color: ${props => props.theme.colors.success};
    }
    
    &.warning {
      background-color: rgba(141, 102, 5, 0.1);
      color: ${props => props.theme.colors.warning};
    }
  }
  
  /* AWS Cards */
  .aws-card {
    background-color: ${props => props.theme.colors.surface};
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: ${props => props.theme.borderRadius.medium};
    transition: all 0.15s ease;
    overflow: hidden;
    
    &:hover {
      box-shadow: ${props => props.theme.shadows.dropdown};
      border-color: ${props => props.theme.colors.primary};
    }
  }
  
  /* AWS Grid */
  .aws-grid {
    display: grid;
    gap: 24px;
    
    &.cols-1 { grid-template-columns: 1fr; }
    &.cols-2 { grid-template-columns: repeat(2, 1fr); }
    &.cols-3 { grid-template-columns: repeat(3, 1fr); }
    &.cols-4 { grid-template-columns: repeat(4, 1fr); }
    
    @media (max-width: ${props => props.theme.breakpoints.md}) {
      &.cols-2,
      &.cols-3,
      &.cols-4 {
        grid-template-columns: 1fr;
      }
    }
  }
  
  /* Theme Toggle */
  .theme-toggle {
    background: transparent;
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: ${props => props.theme.borderRadius.small};
    color: ${props => props.theme.colors.textPrimary};
    padding: 8px 12px;
    cursor: pointer;
    transition: all 0.15s ease;
    
    &:hover {
      background-color: ${props => props.theme.colors.hover};
    }
  }
  
  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.surfaceSecondary};
  }
  
  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 4px;
    
    &:hover {
      background: ${props => props.theme.colors.textTertiary};
    }
  }
`;

// Styled Components
const AppWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.background};
`;

const MainContent = styled.main`
  flex: 1;
`;

// Page transition variants
const pageVariants = {
  initial: { 
    opacity: 0, 
    y: 20
  },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    transition: {
      duration: 0.2,
      ease: "easeIn"
    }
  }
};

function App() {
  // Initialize theme from localStorage or system preference
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const savedTheme = localStorage.getItem('portfolio-theme');
      if (savedTheme) {
        return savedTheme === 'dark';
      }
      // If no saved preference, use system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Failed to load theme preference:', error);
      return false;
    }
  });
  
  // Save theme preference to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('portfolio-theme', isDarkMode ? 'dark' : 'light');
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  }, [isDarkMode]);

  // Optional: Listen for system theme changes (only if no user preference is saved)
  useEffect(() => {
    const handleSystemThemeChange = (e) => {
      const savedTheme = localStorage.getItem('portfolio-theme');
      // Only update if user hasn't set a preference
      if (!savedTheme) {
        setIsDarkMode(e.matches);
      }
    };

    if (window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addListener(handleSystemThemeChange);
      
      return () => {
        mediaQuery.removeListener(handleSystemThemeChange);
      };
    }
  }, []);
  
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };
  
  const theme = getTheme(isDarkMode);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <Router>
          <AppWrapper>
            <ScrollToTop />
            <Header />
            <MainContent>
              <Routes>
                <Route 
                  path="/" 
                  element={
                    <motion.div
                      variants={pageVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                    >
                      <Home />
                    </motion.div>
                  } 
                />
                <Route 
                  path="/about" 
                  element={
                    <motion.div
                      variants={pageVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                    >
                      <About />
                    </motion.div>
                  } 
                />
                <Route 
                  path="/projects" 
                  element={
                    <motion.div
                      variants={pageVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                    >
                      <Projects />
                    </motion.div>
                  } 
                />
                <Route 
                  path="/projects/:id" 
                  element={
                    <motion.div
                      variants={pageVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                    >
                      <ProjectDetail />
                    </motion.div>
                  } 
                />
                <Route 
                  path="/contact" 
                  element={
                    <motion.div
                      variants={pageVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                    >
                      <Contact />
                    </motion.div>
                  } 
                />
              </Routes>
            </MainContent>
            <Footer />
          </AppWrapper>
        </Router>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

export default App;